// BurnTicker — animated counter of total R₿L burned + month/week/% supply.
import React, { useEffect, useRef, useState } from "react";
import { Flame } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { tBurn } from "../../i18n/burn";

const fmt = (n) => Number(n).toLocaleString();

function useCountUp(target, duration = 1200) {
  const [val, setVal] = useState(0);
  const fromRef = useRef(0);
  useEffect(() => {
    const from = fromRef.current;
    if (target === from) return;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

export default function BurnTicker({ stats }) {
  const { lang } = useLanguage();
  const t = (k) => tBurn(lang, k);
  const total = useCountUp(stats?.total_burned_rbl || 0);

  return (
    <div data-testid="burn-ticker">
      <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-orange-300/80">
        <Flame className="h-4 w-4 animate-pulse text-orange-400" /> {t("totalBurned")}
      </p>
      <p className="mt-1 font-mono text-3xl font-black text-white md:text-4xl" data-testid="burn-total">
        {fmt(total)} <span className="text-base text-orange-400">R₿L</span>
      </p>
      {stats && (
        <p className="mt-0.5 font-mono text-[11px] text-white/40">
          {stats.total_burned_usd != null && <>≈ ${fmt(stats.total_burned_usd)} USD · </>}
          {stats.percent_of_supply}% {t("percentSupply").replace("% ", "")}
        </p>
      )}
      {stats?.voluntary_burned_rbl > 0 && (
        <p className="mt-0.5 font-mono text-[10px] text-white/35" data-testid="burn-breakdown">
          {t("proBurns")}: <span className="text-orange-300/80">{fmt(stats.pro_burned_rbl)}</span> ·{" "}
          {t("voluntaryBurns")}: <span className="text-fuchsia-300/80">{fmt(stats.voluntary_burned_rbl)}</span>
        </p>
      )}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-xl bg-white/[0.04] px-3 py-2.5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/35">{t("thisMonth")}</p>
          <p className="mt-0.5 font-mono text-sm font-bold text-orange-300" data-testid="burn-month">
            {stats ? fmt(stats.burned_this_month) : "…"} R₿L
          </p>
        </div>
        <div className="rounded-xl bg-white/[0.04] px-3 py-2.5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/35">{t("thisWeek")}</p>
          <p className="mt-0.5 font-mono text-sm font-bold text-orange-300" data-testid="burn-week">
            {stats ? fmt(stats.burned_this_week) : "…"} R₿L
          </p>
        </div>
      </div>
    </div>
  );
}
